// Helpers for the Tauri desktop build. In the browser these are inert: isTauri()
// is false, so the integration effect in App.tsx never imports the Tauri APIs.

export interface NativeFilePayload {
  path: string;
  name: string;
  content: string;
}

/** True when running inside the Tauri WebView (desktop app), false in a browser. */
export const isTauri = (): boolean =>
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export type ExportKind = "md" | "html" | "pdf";

const dialogFilters: Record<ExportKind, { name: string; extensions: string[] }> = {
  md: { name: "Markdown", extensions: ["md", "markdown"] },
  html: { name: "HTML", extensions: ["html"] },
  pdf: { name: "PDF", extensions: ["pdf"] },
};

/**
 * Save bytes to a user-chosen location through the native Save dialog. Desktop
 * only — the WebView ignores `<a download>`, so exports must go through Rust.
 * Returns false when the user cancels the dialog.
 */
export const saveFileNative = async (data: Uint8Array, defaultName: string, kind: ExportKind): Promise<boolean> => {
  const [{ save }, { invoke }] = await Promise.all([
    import("@tauri-apps/plugin-dialog"),
    import("@tauri-apps/api/core"),
  ]);
  const path = await save({ defaultPath: defaultName, filters: [dialogFilters[kind]] });
  if (!path) return false; // user cancelled
  await invoke("save_file", { path, contents: Array.from(data) });
  return true;
};

/**
 * Ask where to save a Markdown file and return the chosen path (without writing
 * anything). Desktop only — used when linking an unsaved tab to a real file, so
 * later saves can write straight back to it.
 */
export const pickMarkdownSavePath = async (defaultPath: string): Promise<string | null> => {
  const { save } = await import("@tauri-apps/plugin-dialog");
  const path = await save({ defaultPath, filters: [dialogFilters.md] });
  return path ?? null;
};
